import React, { useState, useEffect, useRef } from "react";
import {
  Approach1, Approach2, Approach3,
  Approach4, Approach5, Approach6,
} from "../../Assets.tsx";

/* Colours: text #4e5356 | buttons #2596be | headings #4573a6
   Fonts  : Ubuntu Bold (headings) / Open Sans Regular (body)
   Idea   : the approach is a real sequence, so it is a timeline that descends the
            page: a thread fills downward as you scroll, each step lights up and its
            card slides in from alternating sides. */

type ApproachItem = {
  src: string;
  title: string;
  text: string;
};

const approachData: ApproachItem[] = [
  { src: Approach1, title: "PRODUCT & MARKET STRATEGY", text: "We help you scale your product globally with expert consulting, empowering businesses to achieve long-term success and sustainable growth." },
  { src: Approach2, title: "UI/UX DESIGN", text: "We craft user-centric UI/UX solutions that enhance engagement and drive business growth." },
  { src: Approach3, title: "NFP & MVP", text: "We transform your ideas into reality, preparing them to thrive in the real world." },
  { src: Approach4, title: "PRODUCT DEVELOPMENT", text: "We don't just build products; we create market leaders. Develop your product with innovation and excellence that set you apart from the competition." },
  { src: Approach5, title: "QA & TESTING", text: "We rigorously test your product in real-world conditions, ensuring a flawless, bug-free experience." },
  { src: Approach6, title: "SUPPORT & MAINTENANCE", text: "We nurture your product with expert care, ensuring seamless performance and long-term reliability." },
];

const STYLES = `
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400&family=Ubuntu:wght@700&display=swap");
.ap{--text:#4e5356;--head:#4573a6;--btn:#2596be;--tint:#eef6fa;--line:#d3e4ed;
  position:relative;overflow:hidden;padding:90px 24px 100px;text-align:center;font-family:"Open Sans",sans-serif;font-weight:400;color:var(--text);
background: #ffffff;

.ap::before {
  content: none;
}
  .ap>*{position:relative}

/* header drops from the top */
.ap-head{animation:apDrop .9s cubic-bezier(.2,.8,.2,1) backwards}
.ap-chip{display:inline-block;margin-bottom:16px;padding:6px 18px;border-radius:50px;background:#fff;border:1px solid var(--line);
  color:var(--head);font-size:12px;letter-spacing:2px;text-transform:uppercase}
.ap-head h1{font-family:"Ubuntu",sans-serif;font-weight:700;font-size:42px;line-height:1.15;margin:0 0 14px;color:var(--head);cursor:default;transition:color .35s,letter-spacing .35s}
.ap-head h1:hover{color:var(--btn);letter-spacing:.6px}
.ap-sub{display:flex;align-items:center;justify-content:center;gap:12px;margin-bottom:64px}
.ap-sub i{display:block;height:2px;width:40px;border-radius:2px;background:var(--btn)}.ap-sub i:last-child{background:var(--head)}
.ap-sub p{margin:0;font-size:15px;color:var(--text)}

/* timeline */
.ap-line{position:relative;max-width:1060px;margin:0 auto}
.ap-thread{position:absolute;left:50%;top:0;bottom:0;width:3px;margin-left:-1.5px;border-radius:3px;background:var(--line)}
.ap-fill{width:100%;height:0;border-radius:3px;background:linear-gradient(180deg,var(--btn),var(--head))}

.ap-step{position:relative;display:grid;grid-template-columns:1fr 64px 1fr;align-items:center;margin-bottom:38px;text-align:left}
.ap-step:last-child{margin-bottom:0}
.ap-node{grid-column:2;grid-row:1;justify-self:center;z-index:2;width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  background:#fff;border:3px solid var(--line);color:var(--head);font-family:"Ubuntu",sans-serif;font-weight:700;font-size:15px;transition:background .5s,border-color .5s,color .5s,box-shadow .5s}
.ap-step.in .ap-node{background:var(--btn);border-color:#fff;color:#fff;box-shadow:0 0 0 4px rgba(37,150,190,.25),0 10px 20px -8px rgba(37,150,190,.7)}
.ap-step.in .ap-node{animation:apPop .6s cubic-bezier(.3,1.6,.5,1) backwards}

.ap-card{position:relative;grid-column:1;grid-row:1;padding:30px 28px 26px;border:1px solid var(--line);border-radius:22px;cursor:default;
  background:linear-gradient(180deg,#4573a6,#2596be) no-repeat top/100% 0,#fff;box-shadow:0 18px 36px -26px rgba(69,115,166,.55);
  transition:background-size .55s cubic-bezier(.2,.8,.2,1),transform .4s cubic-bezier(.3,1.4,.5,1),box-shadow .4s,border-color .4s}
.ap-card::before{content:"";position:absolute;top:50%;right:-32px;width:32px;height:2px;margin-top:-1px;background:var(--line);transition:background .5s}
.ap-step.in .ap-card::before{background:var(--btn)}
.ap-step.odd .ap-card{grid-column:3}
.ap-step.odd .ap-card::before{right:auto;left:-32px}
.ap-card:hover{background-size:100% 100%,auto;transform:translateY(-8px);border-color:transparent;box-shadow:0 30px 50px -22px rgba(37,150,190,.7)}

.ap-icon{width:62px;height:62px;border-radius:16px;margin-bottom:18px;display:flex;align-items:center;justify-content:center;background:var(--tint);transition:background .4s}
.ap-icon img{width:32px;height:32px;transition:filter .3s,transform .4s}
.ap-card:hover .ap-icon{background:#fff;box-shadow:0 10px 22px -8px rgba(0,0,0,.35)}
.ap-card:hover .ap-icon img{transform:translateY(3px) scale(1.08)}
.ap-card h3{font-family:"Ubuntu",sans-serif;font-weight:700;font-size:16px;letter-spacing:.6px;line-height:1.4;margin:0 0 10px;color:var(--head);transition:color .35s}
.ap-card p{font-size:14.5px;line-height:1.75;margin:0 0 18px;color:var(--text);transition:color .35s}
.ap-card:hover h3,.ap-card:hover p{color:#fff}
.ap-bar{height:3px;width:30px;border-radius:2px;background:var(--btn);transition:width .4s,background .3s}
.ap-card:hover .ap-bar{width:70px;background:#fff}

/* reveal: cards slide in from alternating sides */
.ap-step:not(.in) .ap-card{opacity:0}
.ap-step:not(.in) .ap-node{opacity:1}
.ap-step.in .ap-card{animation:apFromL .9s cubic-bezier(.2,.8,.2,1) backwards}
.ap-step.odd.in .ap-card{animation-name:apFromR}
@keyframes apFromL{from{opacity:0;transform:translateX(-90px)}}
@keyframes apFromR{from{opacity:0;transform:translateX(90px)}}
@keyframes apDrop{from{opacity:0;transform:translateY(-50px)}}
@keyframes apPop{from{transform:scale(0)}}

@media(max-width:820px){
  .ap{padding:70px 16px 80px}.ap-head h1{font-size:30px}.ap-sub{margin-bottom:44px}
  .ap-thread{left:22px;margin-left:-1.5px}
  .ap-step,.ap-step.odd{grid-template-columns:44px 1fr;column-gap:22px}
  .ap-node{grid-column:1;align-self:start;margin-top:18px}
  .ap-card,.ap-step.odd .ap-card{grid-column:2}
  .ap-card::before,.ap-step.odd .ap-card::before{right:auto;left:-22px;width:22px}
  .ap-step.in .ap-card,.ap-step.odd.in .ap-card{animation-name:apDrop}
}
@media(prefers-reduced-motion:reduce){
  .ap *,.ap *::before,.ap *::after{animation:none!important;transition:none!important}
  .ap .ap-step .ap-card{opacity:1}
}
`;

const Step = ({ item, index }: { item: ApproachItem; index: number }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setOn(true); observer.disconnect(); } },
      { threshold: 0.35 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={"ap-step" + (index % 2 ? " odd" : "") + (on ? " in" : "")}>
      <div className="ap-node">{index + 1}</div>
      <div className="ap-card">
        <div className="ap-icon"><img src={item.src} alt={item.title} /></div>
        <h3>{item.title}</h3>
        <p>{item.text}</p>
        <div className="ap-bar" />
      </div>
    </div>
  );
};

const OurApproach: React.FC = () => {
  const wrap = useRef<HTMLDivElement | null>(null);
  const fill = useRef<HTMLDivElement | null>(null);

  // the thread fills downward as you scroll through the steps
  useEffect(() => {
    const onScroll = () => {
      if (!wrap.current || !fill.current) return;
      const r = wrap.current.getBoundingClientRect();
      const p = Math.min(Math.max((window.innerHeight * 0.6 - r.top) / r.height, 0), 1);
      fill.current.style.height = p * 100 + "%";
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div className="ap">
      <style>{STYLES}</style>

      <div className="ap-head">
        <div className="ap-chip">How We Work</div>
        <h1>Our Approach To The Projects</h1>
        <div className="ap-sub">
          <i />
          <p>Structured. Strategic. Successful.</p>
          <i />
        </div>
      </div>

      <div className="ap-line" ref={wrap}>
        <div className="ap-thread"><div className="ap-fill" ref={fill} /></div>
        {approachData.map((item, index) => (
          <Step key={index} item={item} index={index} />
        ))}
      </div>
    </div>
  );
};

export default OurApproach;